import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { AnimatePresence } from 'framer-motion';
import { IconArrowLeft, IconMoodSad, IconPhoto, IconDownload } from '@tabler/icons-react';
import { useFileList, flattenPages } from '../hooks/useFileList.ts';
import { useExifOne } from '../hooks/useExif.ts';
import { useOriginalSizes } from '../hooks/useOriginalSizes.ts';
import { useShallow } from 'zustand/react/shallow';
import { useGalleryStore } from '../state/galleryStore.ts';
import { filterAndSort } from '../lib/selectors.ts';
import { gridColumnsClass } from '../lib/gridColumns.ts';
import { JustifiedGallery } from '../components/gallery/JustifiedGallery.tsx';
import { FileListTable } from '../components/gallery/FileListTable.tsx';
import { SearchFilterBar } from '../components/gallery/SearchFilterBar.tsx';
import { MediaViewer } from '../components/media/MediaViewer.tsx';
import { BulkActionBar } from '../components/gallery/BulkActionBar.tsx';
import { DownloadProgress } from '../components/gallery/DownloadProgress.tsx';
import { MoveToEventDialog } from '../components/gallery/MoveToEventDialog.tsx';
import { useMove } from '../hooks/useMove.ts';
import { useDownload } from '../hooks/useDownload.ts';
import { SkeletonGrid } from '../components/ui/Skeleton.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Spinner } from '../components/ui/Spinner.tsx';
import { getShareLinks } from '../lib/apiClient.ts';
import { toast } from '../state/toastStore.ts';
import type { MediaItem } from '../lib/types.ts';

/** Map the 1-6 "photo size" slider to a justified-row target height (px). */
function rowHeightFor(sizeStep: number): number {
  const heights: Record<number, number> = { 1: 140, 2: 180, 3: 220, 4: 280, 5: 340, 6: 420 };
  return heights[sizeStep] ?? 240;
}

export function EventPage() {
  const { event = '' } = useParams();
  const decodedEvent = decodeURIComponent(event);
  const { data, isLoading, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } =
    useFileList(decodedEvent);

  // Select reactive view state shallowly (re-render only when these change),
  // and pull stable action references separately. Calling useGalleryStore()
  // with NO selector returns a new snapshot every state change and, combined
  // with an effect dependency, causes an infinite render loop (React #185).
  const store = useGalleryStore(
    useShallow((s) => ({
      viewMode: s.viewMode,
      gridColumns: s.gridColumns,
      sortBy: s.sortBy,
      sortDescending: s.sortDescending,
      search: s.search,
      dateRange: s.dateRange,
      mediaType: s.mediaType,
      selection: s.selection,
    })),
  );
  const setGridColumns = useGalleryStore((s) => s.setGridColumns);
  const toggleSelected = useGalleryStore((s) => s.toggleSelected);
  const clearSelection = useGalleryStore((s) => s.clearSelection);

  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const [moveOpen, setMoveOpen] = useState(false);
  const { ref: sentinelRef, inView } = useInView({ rootMargin: '400px' });
  const move = useMove(decodedEvent);
  const download = useDownload();

  // Reset selection when leaving the event. clearSelection is a stable
  // Zustand action reference, so this effect runs only on event change.
  useEffect(() => () => clearSelection(), [decodedEvent, clearSelection]);

  const rawItems = useMemo(() => flattenPages(data?.pages), [data]);

  // The listing is of thumbnails, so item.size is the thumbnail size; fetch the
  // originals' sizes (one cheap list call) so the UI shows the real file size.
  const { data: originalSizes } = useOriginalSizes(decodedEvent);

  // Gallery sorts/filters by the filename timestamp already on each item — we
  // do NOT bulk-fetch EXIF for every thumbnail (that was hundreds of calls per
  // event). EXIF is fetched lazily for just the open lightbox photo below.
  const items: MediaItem[] = useMemo(() => {
    const enriched = rawItems.map((item) => {
      const originalSize = originalSizes?.get(item.key);
      return originalSize != null ? { ...item, size: originalSize } : item;
    });
    return filterAndSort(
      enriched,
      { search: store.search, dateRange: store.dateRange, mediaType: store.mediaType },
      store.sortBy,
      store.sortDescending,
    );
  }, [rawItems, originalSizes, store.search, store.dateRange, store.mediaType, store.sortBy, store.sortDescending]);

  // EXIF only for the photo currently open in the viewer (its info panel).
  const openItem = viewerIndex !== null ? items[viewerIndex] : null;
  const { data: openExif } = useExifOne(openItem?.key ?? null);

  // Infinite scroll.
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleShare = async (item: MediaItem) => {
    try {
      const links = await getShareLinks([item.key]);
      const url = links[0]?.url;
      if (url) {
        await navigator.clipboard.writeText(url);
        toast.success('Share link copied to clipboard');
      } else {
        toast.error('Could not create share link');
      }
    } catch {
      toast.error('Sharing failed');
    }
  };

  const handleShareSelected = async () => {
    const keys = [...store.selection];
    if (keys.length === 0) return;
    try {
      const links = await getShareLinks(keys);
      if (links.length > 0) {
        await navigator.clipboard.writeText(links.map((l) => l.url).join('\n'));
        toast.success(`${links.length} share link${links.length !== 1 ? 's' : ''} copied`);
      } else {
        toast.error('Could not create share links');
      }
    } catch {
      toast.error('Sharing failed');
    }
  };

  // Single photo → direct download; anything else → server-built ZIP.
  const handleDownloadOne = (item: MediaItem) => download.downloadOne(item.key, item.displayName);

  const handleDownloadSelected = () => {
    const keys = [...store.selection];
    if (keys.length === 0) return;
    if (keys.length === 1) {
      const only = items.find((i) => i.key === keys[0]);
      download.downloadOne(keys[0], only?.displayName);
    } else {
      download.downloadArchive({ keys });
    }
  };

  const handleDownloadAlbum = () => download.downloadArchive({ event: decodedEvent });

  const handleMoveConfirm = (destinationEvent: string) => {
    const keys = [...store.selection];
    move.mutate(
      { originalKeys: keys, destinationEvent },
      {
        onSuccess: (results) => {
          const failed = results.filter((r) => r.status === 'error').length;
          if (failed > 0) {
            toast.error(`Moved with ${failed} error${failed !== 1 ? 's' : ''}`);
          } else {
            toast.success(`Moved ${keys.length} item${keys.length !== 1 ? 's' : ''}`);
          }
          setMoveOpen(false);
          clearSelection();
        },
        onError: (err) => {
          toast.error(
            err instanceof Error ? `Move failed: ${err.message}` : 'Move failed',
          );
        },
      },
    );
  };

  const selectionActive = store.selection.size > 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Link
          to="/events"
          className="grid h-9 w-9 place-items-center rounded-md border border-line bg-surface text-fg-2 shadow-sm transition duration-200 ease-standard hover:bg-grid active:scale-[0.97]"
          aria-label="Back to events"
        >
          <IconArrowLeft className="h-5 w-5" />
        </Link>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-2xl font-semibold text-fg">{decodedEvent}</h1>
          <p className="text-sm text-fg-2">
            {items.length} item{items.length !== 1 ? 's' : ''}
            {selectionActive && ` · ${store.selection.size} selected`}
          </p>
        </div>
        {items.length > 0 && (
          <Button
            variant="secondary"
            onClick={handleDownloadAlbum}
            disabled={download.busy}
            title="Download all photos in this event as a ZIP"
          >
            {download.busy ? <Spinner className="h-4 w-4" /> : <IconDownload className="h-4 w-4" />}
            Download album
          </Button>
        )}
      </div>

      <SearchFilterBar />

      {/* Photo-size control (grid only): larger value = taller rows = bigger photos. */}
      {store.viewMode === 'grid' && (
        <div className="flex items-center gap-2 text-xs text-fg-2">
          <span>Photo size</span>
          <input
            type="range"
            min={1}
            max={6}
            value={store.gridColumns}
            onChange={(e) => setGridColumns(Number(e.target.value))}
            aria-label="Photo size"
            className="accent-accent"
          />
        </div>
      )}

      {isLoading && <SkeletonGrid count={12} columnsClass={gridColumnsClass(store.gridColumns)} />}

      {isError && (
        <EmptyState
          icon={IconMoodSad}
          title="Couldn't load photos"
          description={error instanceof Error ? error.message : 'Please try again.'}
          action={<Button variant="primary" onClick={() => refetch()}>Retry</Button>}
        />
      )}

      {!isLoading && !isError && items.length === 0 && (
        <EmptyState
          icon={IconPhoto}
          title="No photos match"
          description="Try adjusting your search or filters, or upload something new."
        />
      )}

      {items.length > 0 && store.viewMode === 'list' && (
        <FileListTable
          items={items}
          selection={store.selection}
          selectionActive={selectionActive}
          onOpen={(i) => setViewerIndex(i)}
          onToggleSelect={(key) => toggleSelected(key)}
        />
      )}

      {items.length > 0 && store.viewMode === 'grid' && (
        <JustifiedGallery
          items={items}
          selection={store.selection}
          selectionActive={selectionActive}
          targetRowHeight={rowHeightFor(store.gridColumns)}
          onOpen={(i) => setViewerIndex(i)}
          onToggleSelect={(key) => toggleSelected(key)}
        />
      )}

      {/* Infinite-scroll sentinel */}
      <div ref={sentinelRef} className="h-10" />
      {isFetchingNextPage && (
        <div className="flex justify-center py-4 text-accent">
          <Spinner />
        </div>
      )}
      {!hasNextPage && items.length > 0 && (
        <p className="py-4 text-center text-sm text-muted">You've reached the end</p>
      )}

      <AnimatePresence>
        {viewerIndex !== null && items[viewerIndex] && (
          <MediaViewer
            items={items}
            index={viewerIndex}
            onIndexChange={setViewerIndex}
            onClose={() => setViewerIndex(null)}
            onShare={handleShare}
            onDownload={handleDownloadOne}
            exifFor={(key) => (key === openItem?.key && openExif ? openExif : undefined)}
            onNearEnd={() => {
              if (hasNextPage && !isFetchingNextPage) fetchNextPage();
            }}
          />
        )}
      </AnimatePresence>

      <BulkActionBar
        event={decodedEvent}
        items={items}
        onShare={handleShareSelected}
        onDownload={handleDownloadSelected}
        downloading={download.busy}
        onMove={() => setMoveOpen(true)}
      />

      <DownloadProgress
        phase={download.phase}
        done={download.done}
        total={download.total}
        onCancel={download.cancel}
      />

      <MoveToEventDialog
        open={moveOpen}
        count={store.selection.size}
        sourceEvent={decodedEvent}
        busy={move.isPending}
        onClose={() => setMoveOpen(false)}
        onConfirm={handleMoveConfirm}
      />
    </div>
  );
}
